/* 차트 렌더러 — valuation_agent/static/js/charts.js 이식 (Phase 3-B).
   toOption 의 출력은 바닐라와 동일해야 한다 (tests/charts.spec.ts 가 같은 spec 으로 대조).
   변경점: window.echarts(CDN) 대신 npm echarts 를 dynamic import(코드 스플리팅),
          hydrate 시 떨어져 나간 인스턴스 정리(prune) — React 리렌더로 DOM 이
          통째로 교체되는 환경에서의 누수 방지. 나머지 로직·마크업은 바닐라 그대로. */

import { logoFailed, logoUrl, markLogoFailed } from "./companyLogo";

/* 서버 차트 스펙 — 바닐라와 동일하게 느슨한 JSON 을 그대로 받는다 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Any = any;

type EchartsNs = typeof import("echarts");
type EchartsInst = ReturnType<EchartsNs["init"]>;

interface ChartInstance {
  inst: EchartsInst;
  spec: Any;
  el: HTMLElement;
  ro: ResizeObserver | null;
}

const instances: ChartInstance[] = [];

const tok = (n: string): string => {
  try {
    return getComputedStyle(document.documentElement).getPropertyValue(n).trim();
  } catch {
    return ""; // 테스트(node) 환경 — 바닐라 스텁과 동일하게 빈 값
  }
};
const isDark = (): boolean => {
  try {
    return document.documentElement.getAttribute("data-theme") === "dark";
  } catch {
    return false;
  }
};

// 채도 높은 팔레트 — 파스텔은 데이터가 배경에 묻힌다는 피드백(2026-08-03)
const PASTEL_LIGHT = ["#3D6FD6", "#2FA88C", "#E08A2E", "#8256C8", "#D64F4F", "#63993F", "#C75E9B", "#3E8FB8"];
const PASTEL_DARK = ["#6E9BE8", "#4FC0A4", "#EFA858", "#A585DD", "#E37272", "#8CBB6C", "#DB88B6", "#6BAED0"];
const palette = () => (isDark() ? PASTEL_DARK : PASTEL_LIGHT);
const upColor = () => (isDark() ? "#E36B6B" : "#D64545"); // 국내 관행: 상승 적색
const downColor = () => (isDark() ? "#6E9BE8" : "#3D6FC6"); //            하락 청색

let fetcher: ((url: string) => Promise<Any>) | null = null; // ChatPage 가 주입 (인증 헤더 포함 fetch)
export function setFetcher(fn: (url: string) => Promise<Any>) {
  fetcher = fn;
}

/* ── echarts 지연 로딩 (코드 스플리팅 — 메인 번들 비대 방지) ── */
let echartsNs: EchartsNs | null = null;
let echartsPromise: Promise<EchartsNs> | null = null;
function ensureEcharts(): Promise<EchartsNs> {
  if (!echartsPromise) {
    echartsPromise = import("echarts").then((m) => {
      echartsNs = m;
      return m;
    });
  }
  return echartsPromise;
}

/* ── ECharts 옵션 ───────────────────────────────────── */
function baseOption(): Any {
  const ink = tok("--ink"), line = tok("--line"), paper = tok("--paper");
  return {
    color: palette(),
    textStyle: { fontFamily: '"Pretendard Variable", Pretendard, sans-serif', color: ink },
    tooltip: { backgroundColor: paper, borderColor: line, textStyle: { color: ink, fontSize: 12 }, confine: true },
    grid: { left: 8, right: 12, top: 32, bottom: 4, containLabel: true },
    animationDuration: 400,
  };
}
function axis(over?: Any): Any {
  const muted = tok("--muted"), line = tok("--line");
  return Object.assign(
    {
      axisLine: { lineStyle: { color: line } },
      axisTick: { show: false },
      axisLabel: { color: muted, fontSize: 10.5 },
      splitLine: { lineStyle: { color: line, opacity: 0.5 } },
    },
    over || {},
  );
}
const legendOpt = (extra?: Any): Any =>
  Object.assign(
    { top: 0, right: 0, icon: "roundRect", itemWidth: 9, itemHeight: 9,
      textStyle: { color: tok("--muted"), fontSize: 11 } },
    extra || {},
  );

// 큰 금액을 통화에 맞게 축약. currency 는 백엔드 스펙의 값(KRW·USD)이고
// 비어 있으면(지수 등) 단위 없이 숫자만 — 종전에는 전부 '원' 으로 굳어 있어
// TSLA 주가 250달러가 "250원", 코스피 지수 2,500 이 "2,500원" 으로 보였다.
function fmtMoney(v: Any, currency?: string): string {
  if (v == null || isNaN(v)) return "-";
  const a = Math.abs(v);
  if (currency === "USD") {
    if (a >= 1e9) return "$" + (v / 1e9).toFixed(1) + "B";
    if (a >= 1e6) return "$" + (v / 1e6).toFixed(1) + "M";
    if (a >= 1e3) return "$" + (v / 1e3).toFixed(1) + "K";
    return "$" + v.toLocaleString();
  }
  if (currency === "KRW") {
    if (a >= 1e12) return (v / 1e12).toFixed(1) + "조원";
    if (a >= 1e8) return (v / 1e8).toFixed(1) + "억원";
    return v.toLocaleString() + "원";
  }
  // 통화 미상 — 단위를 붙이지 않는다
  if (a >= 1e12) return (v / 1e12).toFixed(1) + "조";
  if (a >= 1e8) return (v / 1e8).toFixed(1) + "억";
  return v.toLocaleString();
}

// 툴팁 포맷 — 표에 쓰는 표기와 맞춘다(조/억원, 비율은 %)
function tooltipFormatter(spec: Any) {
  return (params: Any) => {
    const arr = Array.isArray(params) ? params : [params];
    if (!arr.length) return "";
    const rows = arr
      .map((p: Any) => {
        const v = Array.isArray(p.value) ? p.value[1] : p.value;
        // 백분율 판정: spec.pct(등락률 — 부호를 붙인다) · 계열 이름의 %·률·율 ·
        // y 축 라벨이 '%' 인 차트. 계열 이름에 단위를 넣지 않는 차트(뉴스 언급 추이)는
        // yLabel 이 단위를 말한다 — 이름에 '%' 를 붙이면 범례마다 같은 말이 반복된다.
        const pct = spec.pct || spec.yLabel === "%"
          || (p.seriesName && /률|율|%/.test(p.seriesName));
        const sign = spec.pct && v > 0 ? "+" : "";
        const shown = v == null ? "-" : pct ? sign + Number(v).toFixed(2) + "%"
          : fmtMoney(Number(v), spec.currency);
        // 값 뒤에 덧붙일 보조 수치 — spec.extra[계열명][인덱스] (예: 비중 옆의 기사 수).
        // 없으면 아무것도 붙지 않는다: 이 필드를 안 주는 차트는 그대로다.
        const ex = spec.extra?.[p.seriesName]?.[p.dataIndex];
        const exHtml = ex ? ` <span style="color:${tok("--muted")};font-weight:400">${ex}</span>` : "";
        return `<div style="display:flex;gap:12px;justify-content:space-between">
          <span>${p.marker || ""}${p.seriesName || ""}</span><b>${shown}${exHtml}</b></div>`;
      })
      .join("");
    return `<div style="font-size:12px"><div style="margin-bottom:4px;color:${tok("--muted")}">${arr[0].axisValueLabel || arr[0].name || ""}</div>${rows}</div>`;
  };
}
// 캔들 툴팁 — OHLC 를 한글 라벨로, 이동평균선이 있으면 이어서 표시
function candleTooltipFormatter(currency?: string) {
  return (params: Any) => {
    const arr = Array.isArray(params) ? params : [params];
    const cd = arr.find((p: Any) => p.seriesType === "candlestick") || arr[0];
    const v = cd && cd.value;
    if (!v) return "";
    const [, o, c, l, h] = v; // ECharts 캔들: [index, open, close, low, high]
    const won = (x: Any) => {
      if (x == null) return "-";
      const nx = Math.round(Number(x)).toLocaleString();
      return currency === "USD" ? "$" + nx : currency === "KRW" ? nx + "원" : nx;
    };
    const line = (k: string, x: Any, mark?: string) => `<div style="display:flex;gap:14px;justify-content:space-between">
        <span>${mark || ""}${k}</span><b>${won(x)}</b></div>`;
    const mas = arr
      .filter((p: Any) => p.seriesType === "line" && p.value != null)
      .map((p: Any) => line(p.seriesName, p.value, p.marker))
      .join("");
    return `<div style="font-size:12px"><div style="margin-bottom:4px;color:${tok("--muted")}">${cd.axisValueLabel || cd.name}</div>
        ${line("시가", o)}${line("고가", h)}${line("저가", l)}${line("종가", c)}
        ${mas ? `<div style="margin-top:5px;padding-top:5px;border-top:1px solid ${tok("--line")}">${mas}</div>` : ""}</div>`;
  };
}

export function toOption(spec: Any): Any {
  const base = baseOption(), colors = palette(), muted = tok("--muted");
  const type = String(spec.type || "bar").toLowerCase();

  if (type === "krmap") {
    // 시도 단위 choropleth — 값이 클수록 진한 브랜드 블루, 지역명+수치 라벨
    const data = spec.data || [];
    const max = Math.max(1, ...data.map((d: Any) => Number(d.value) || 0));
    return Object.assign({}, base, {
      tooltip: Object.assign({}, base.tooltip, {
        trigger: "item",
        formatter: (p: Any) => `${p.name}<br/><b>${(Number(p.value) || 0).toLocaleString("ko-KR")}개</b>`,
      }),
      animationDurationUpdate: 480,
      visualMap: {
        min: 0, max, left: 10, bottom: 10, itemWidth: 10, itemHeight: 80,
        text: [max.toLocaleString(), "0"], textStyle: { color: muted, fontSize: 10 },
        inRange: { color: ["#EAF1FE", "#9DBEF7", "#3D6FD6", "#0C3F9E"] },
      },
      series: [{
        type: "map", map: "kr_sido",
        roam: true, scaleLimit: { min: 1, max: 8 }, // 휠 줌·드래그 팬
        layoutCenter: ["50%", "51%"], layoutSize: "112%", // 박스를 더 채우게
        label: { show: true, fontSize: 9.5, color: tok("--ink"),
                 formatter: (p: Any) => (p.value ? `${p.name}\n${Number(p.value).toLocaleString("ko-KR")}` : p.name) },
        itemStyle: { borderColor: "#ffffff", borderWidth: 0.8 },
        emphasis: { label: { show: true, fontWeight: 700 },
                    itemStyle: { areaColor: "#FFD666" } },
        select: { disabled: true },
        data,
      }],
    });
  }

  if (type === "candle") {
    const d = spec.points || [];
    const mas = spec.mas || [];
    const maSeries = mas.map((m: Any) => ({
      name: m.name, type: "line", data: m.data, showSymbol: false, smooth: false,
      lineStyle: { color: m.color, width: 1.2 }, itemStyle: { color: m.color },
      emphasis: { disabled: true }, z: 3,
    }));
    return Object.assign({}, base, {
      tooltip: Object.assign({}, base.tooltip, { trigger: "axis", axisPointer: { type: "cross" },
                                                 formatter: candleTooltipFormatter(spec.currency) }),
      legend: maSeries.length ? legendOpt({ data: mas.map((m: Any) => m.name) }) : undefined,
      grid: { left: 8, right: 12, top: maSeries.length ? 34 : 16, bottom: 46, containLabel: true },
      xAxis: axis({ type: "category", data: d.map((p: Any) => p.time), splitLine: { show: false } }),
      yAxis: axis({ scale: true, axisLabel: { color: muted, fontSize: 10.5,
                    formatter: (v: Any) => (v >= 10000 ? (v / 10000).toFixed(0) + "만" : v) } }),
      dataZoom: [{ type: "inside", start: 0, end: 100 },
                 { type: "slider", height: 18, bottom: 8, borderColor: tok("--line"),
                   fillerColor: "rgba(127,168,224,.15)", handleStyle: { color: tok("--muted") },
                   textStyle: { color: muted, fontSize: 10 } }],
      series: [{
        type: "candlestick",
        name: "주가",
        data: d.map((p: Any) => [p.open, p.close, p.low, p.high]),
        itemStyle: { color: upColor(), color0: downColor(),
                     borderColor: upColor(), borderColor0: downColor() },
        z: 2,
      }].concat(maSeries),
    });
  }

  if (type === "pie" || type === "donut") {
    return Object.assign({}, base, {
      tooltip: Object.assign({}, base.tooltip, { trigger: "item" }),
      legend: legendOpt(),
      series: [{ type: "pie", radius: type === "donut" ? ["45%", "72%"] : "70%",
                 center: ["50%", "56%"], avoidLabelOverlap: true,
                 label: { color: tok("--ink"), fontSize: 11 },
                 data: (spec.data || []).map((x: Any) => ({ name: x.name, value: x.value })) }],
    });
  }

  if (type === "scatter") {
    return Object.assign({}, base, {
      tooltip: Object.assign({}, base.tooltip, { trigger: "item" }),
      xAxis: axis({ type: "value", name: spec.xLabel || "" }),
      yAxis: axis({ type: "value", name: spec.yLabel || "" }),
      series: [{ type: "scatter", symbolSize: 12, itemStyle: { color: colors[0], opacity: 0.8 },
                 data: (spec.data || []).map((x: Any) => [x.x, x.y, x.name]),
                 label: { show: true, formatter: (p: Any) => p.data[2] || "", position: "top",
                          color: muted, fontSize: 10 } }],
    });
  }

  // bar / line / area / combo
  const twoAxis = (spec.series || []).some((sr: Any) => sr.yAxis === 1);
  const yFmt = spec.pct ? (v: Any) => (v > 0 ? "+" : "") + v + "%"
    : spec.moneyAxis ? (v: Any) => fmtMoney(v, spec.currency).replace("원", "").replace("$", "")
    : spec.compare ? (v: Any) => (v >= 10000 ? (v / 10000).toFixed(0) + "만" : v)
    : undefined;
  const yAxes = [axis({ type: "value", name: spec.pct ? "" : (spec.yLabel || ""), scale: !!spec.scaleY,
                        nameTextStyle: { color: muted, fontSize: 10.5 },
                        axisLabel: { color: muted, fontSize: 10.5, formatter: yFmt } })];
  // 보조축은 이름을 달지 않는다 — 범례와 겹친다. 단위는 눈금 라벨에 붙인다.
  if (twoAxis) yAxes.push(axis({ type: "value", splitLine: { show: false },
                                 axisLabel: { color: muted, fontSize: 10.5,
                                   formatter: (v: Any) => v + (spec.y2Label || "") } }));

  const series = (spec.series || []).map((sr: Any, i: number) => {
    const st = String(sr.type || (type === "combo" ? (i === 0 ? "bar" : "line") : type)).toLowerCase();
    const color = sr.color || colors[i % colors.length];
    const common = { name: sr.name, yAxisIndex: sr.yAxis === 1 && twoAxis ? 1 : 0, data: sr.data || [] };
    if (st === "bar") {
      return Object.assign(common, { type: "bar", barMaxWidth: 26, barGap: "18%",
                                     itemStyle: { color, borderRadius: [3, 3, 0, 0] } });
    }
    return Object.assign(common, {
      // spec.smooth 로 곡선 여부를 정할 수 있다 — 값이 없으면 예전대로.
      // 촘촘한 시계열은 곡선으로 이으면 없던 봉우리가 생긴다(뉴스 언급 추이).
      type: "line", smooth: spec.smooth ?? !spec.compare,
      showSymbol: !spec.compare && (sr.data || []).length <= 24, symbolSize: 5,
      connectNulls: !!spec.compare,
      lineStyle: { color, width: spec.compare ? 1.6 : 2 }, itemStyle: { color },
      areaStyle: (st === "area" || spec.area) ? { opacity: 0.12, color } : undefined,
    });
  });

  if (spec.pct && series.length) {
    series[0] = Object.assign({}, series[0], {
      markLine: { silent: true, symbol: "none",
                  lineStyle: { color: tok("--line-strong") || muted, width: 1, type: "dashed" },
                  label: { show: false }, data: [{ yAxis: 0 }] },
    });
  }
  // spec.zoom — 점이 많은 시계열(뉴스 언급 추이 등)에서 구간을 좁혀 본다.
  // 휠·드래그(inside) + 하단 슬라이더. 캔들 차트와 같은 모양으로 맞춘다.
  const zoom = spec.zoom
    ? [{ type: "inside", start: spec.zoomStart ?? 0, end: 100 },
       { type: "slider", height: 18, bottom: 8, borderColor: tok("--line"),
         fillerColor: "rgba(127,168,224,.15)", handleStyle: { color: tok("--muted") },
         textStyle: { color: muted, fontSize: 10 } }]
    : undefined;
  return Object.assign({}, base, {
    grid: spec.compare
      ? { left: 8, right: 12, top: 12, bottom: 4, containLabel: true }
      : zoom
        ? { left: 8, right: 12, top: 32, bottom: 34, containLabel: true } // 슬라이더 자리
        : base.grid,
    tooltip: Object.assign({}, base.tooltip,
      { trigger: "axis", axisPointer: { type: spec.compare ? "line" : "shadow" },
        formatter: tooltipFormatter(spec) }),
    legend: (series.length > 1 && !spec.compare) ? legendOpt() : undefined,
    xAxis: axis({ type: "category", data: spec.x || [],
                  boundaryGap: series.some((sr: Any) => sr.type === "bar"), splitLine: { show: false } }),
    yAxis: yAxes,
    dataZoom: zoom,
    series,
  });
}

// 대한민국 시도 경계 — krmap 차트가 처음 필요할 때 한 번만 불러 등록한다.
// (광주·전남 통합 행정구역이 병합된 전처리본, static/geo/kr_sido.json)
// krRegionBox: 지역명 → {center, span} — 클릭 줌인의 목표 좌표 계산용.
let krMapPromise: Promise<boolean> | null = null;
const krRegionBox: Record<string, { center: [number, number]; span: [number, number] }> = {};
function ensureKrMap(): Promise<boolean> {
  if (!krMapPromise) {
    krMapPromise = Promise.all([
      ensureEcharts(),
      fetch("/static/geo/kr_sido.json").then((r) => {
        if (!r.ok) throw new Error("geo " + r.status);
        return r.json();
      }),
    ])
      .then(([ec, gj]: [EchartsNs, Any]) => {
        (gj.features || []).forEach((f: Any) => {
          let x0 = 180, x1 = -180, y0 = 90, y1 = -90;
          (f.geometry.coordinates || []).forEach((poly: Any) =>
            poly.forEach((ring: Any) =>
              ring.forEach(([x, y]: [number, number]) => {
                if (x < x0) x0 = x;
                if (x > x1) x1 = x;
                if (y < y0) y0 = y;
                if (y > y1) y1 = y;
              }),
            ),
          );
          krRegionBox[f.properties.name] = { center: [(x0 + x1) / 2, (y0 + y1) / 2],
                                             span: [Math.max(0.05, x1 - x0), Math.max(0.05, y1 - y0)] };
        });
        ec.registerMap("kr_sido", gj);
        return true;
      })
      .catch(() => {
        krMapPromise = null;
        return false;
      });
  }
  return krMapPromise;
}
// 대한민국 전체 경도·위도 스팬 — 클릭한 지역이 화면을 채우는 zoom 배율 계산의 기준
const KR_SPAN: [number, number] = [8.6, 5.6];
function krZoomFor(name: string): { center: [number, number]; zoom: number } | null {
  const b = krRegionBox[name];
  if (!b) return null;
  const z = Math.min(KR_SPAN[0] / b.span[0], KR_SPAN[1] / b.span[1]) * 0.62;
  return { center: b.center, zoom: Math.max(1.6, Math.min(6, z)) };
}

function draw(el: HTMLElement, spec: Any) {
  if (!echartsNs) {
    // CDN 폴링(setTimeout 재시도) 대신 dynamic import 완료 후 재진입.
    // 로딩 사이 메시지 리렌더가 innerHTML 을 다시 적용해 el 이 DOM 에서
    // 떨어져 나갔을 수 있다 — 그때 포기하면 빈 박스가 남는다(지도 실측).
    // 새 박스를 hydrate 로 다시 찾아 그린다.
    void ensureEcharts().then(() => {
      if (el.isConnected) draw(el, spec);
      else hydrate();
    });
    return;
  }
  const prev = instances.findIndex((x) => x.el === el);
  if (prev >= 0) dropInstance(prev);
  const inst = echartsNs.init(el, null, { renderer: "svg" });
  // 버전 스큐 방어 — 서버가 이 프론트보다 새 차트 타입을 보내면(배포 중 열려 있던 탭)
  // 빈 박스 대신 새로고침 안내를 보여준다. 알려진 타입 목록은 새 타입 추가 시 함께 갱신.
  const KNOWN = ["bar", "line", "area", "combo", "candle", "price", "financials", "krmap"];
  if (!KNOWN.includes(String(spec.type || "bar").toLowerCase())) {
    el.innerHTML = '<div style="padding:24px;text-align:center;font-size:12.5px;color:var(--muted)">'
      + "이 차트는 새 버전에서 지원됩니다 — 페이지를 새로고침해 주세요.</div>";
    return;
  }
  if (String(spec.type || "").toLowerCase() === "krmap") {
    void ensureKrMap().then((ok) => {
      if (!ok) { el.textContent = "지도 데이터를 불러오지 못했습니다."; return; }
      if (!el.isConnected) { hydrate(); return; }   // 지도 로딩 사이 리렌더 — 새 박스로 재진입
      try { inst.setOption(toOption(spec)); } catch { return; }
      // 지역 클릭 = 그 지역으로 확대(같은 지역 재클릭 = 전국 복귀) + '전국 보기' 리셋 버튼
      const boxEl = (el.closest(".chart-box") || el.parentElement) as HTMLElement;
      let rst = boxEl.querySelector(".map-reset") as HTMLButtonElement | null;
      if (!rst) {
        rst = document.createElement("button");
        rst.type = "button"; rst.className = "map-reset"; rst.hidden = true;
        rst.textContent = "전국 보기";
        boxEl.appendChild(rst);
      }
      let zoomed: string | null = null;
      const KR_CENTER = [127.75, 36.1];
      function resetView() {
        zoomed = null;
        rst!.hidden = true;
        try { inst.setOption({ series: [{ zoom: 1, center: KR_CENTER }] }); } catch { /* noop */ }
      }
      rst.onclick = resetView;
      inst.off("click");
      inst.on("click", (p: Any) => {
        if (!p || p.componentType !== "series") return;
        if (zoomed === p.name) { resetView(); return; }
        const t = krZoomFor(p.name);
        if (!t) return;
        zoomed = p.name;
        rst!.hidden = false;
        try { inst.setOption({ series: [{ zoom: t.zoom, center: t.center }] }); } catch { /* noop */ }
      });
      // 휠·드래그로 직접 움직였을 때도 복귀 버튼을 노출
      inst.off("georoam");
      inst.on("georoam", () => {
        if (zoomed === null) zoomed = "_manual";
        rst!.hidden = false;
      });
    });
  } else inst.setOption(toOption(spec));

  // 확대 구간을 바깥에 알린다. spec 은 JSON(data-chart 속성)이라 콜백을 실을 수
  // 없어서, 차트 박스에 DOM 이벤트를 띄운다 — 페이지는 그 이벤트만 들으면 된다.
  // (뉴스 트렌드: 차트를 드래그하면 기간 입력이 따라 바뀐다)
  if (spec.zoom) {
    const xs: Any[] = spec.x || [];
    const target = (el.closest(".chart-box") || el) as HTMLElement;
    inst.off("dataZoom");
    inst.on("dataZoom", () => {
      // 이벤트 인자는 slider/inside 에 따라 모양이 달라 신뢰하기 어렵다 —
      // 실제 축이 무엇을 담고 있는지 옵션에서 직접 읽는다.
      let s = 0, e = 100;
      try {
        const dz = (inst.getOption() as Any).dataZoom || [];
        if (dz.length) { s = Number(dz[0].start ?? 0); e = Number(dz[0].end ?? 100); }
      } catch { return; }
      if (!xs.length || !isFinite(s) || !isFinite(e)) return;
      const last = xs.length - 1;
      const i0 = Math.max(0, Math.min(last, Math.round((s / 100) * last)));
      const i1 = Math.max(0, Math.min(last, Math.round((e / 100) * last)));
      target.dispatchEvent(new CustomEvent("va:zoom", {
        bubbles: true,
        detail: { from: String(xs[Math.min(i0, i1)]), to: String(xs[Math.max(i0, i1)]),
                  full: s <= 0 && e >= 100 },
      }));
    });
  }

  // 세션 복원 직후처럼 레이아웃이 잡히기 전에 그리면 좁은 폭으로 렌더된 채
  // 남는다(window resize 에만 반응하므로). 컨테이너 크기를 직접 관찰해서
  // 폭이 바뀌면 차트도 따라 늘린다. 사이드바 리사이즈에도 함께 반응한다.
  let ro: ResizeObserver | null = null;
  if (typeof ResizeObserver !== "undefined") {
    ro = new ResizeObserver(() => {
      try { inst.resize(); } catch { /* noop */ }
    });
    ro.observe(el);
  }
  instances.push({ inst, spec, el, ro });
}

function dropInstance(i: number) {
  try { if (instances[i].ro) instances[i].ro.disconnect(); } catch { /* noop */ }
  try { instances[i].inst.dispose(); } catch { /* noop */ }
  instances.splice(i, 1);
}

// 테마가 바뀌면 살아있는 차트를 다시 그린다 — 팔레트·축·격자 색은 그리는 순간의
// 테마로 굳는다. 안 그리면 다크에서 그린 차트가 라이트 전환 뒤 연하게 남는다
// (코스피 라인이 다크용 #6E9BE8 그대로 — Bishop 제보). spec 은 instances 에
// 있으므로 같은 자리에 다시 그리면 된다(draw 가 기존 인스턴스를 교체한다).
if (typeof MutationObserver !== "undefined" && typeof document !== "undefined") {
  new MutationObserver(() => {
    for (const c of [...instances]) {
      if (c.el.isConnected) draw(c.el, c.spec);
    }
  }).observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
}

/* ── 스펙 변환 ──────────────────────────────────────── */
// 재무 시계열 → 매출·순이익 막대 + 순이익률 라인
function financialsSpec(points: Any[], currency?: string): Any {
  return {
    type: "combo", moneyAxis: true, currency,
    x: points.map((p) => p.label),
    series: [
      { name: "매출", type: "bar", data: points.map((p) => p.revenue), color: palette()[0] },
      { name: "순이익", type: "bar", data: points.map((p) => p.netIncome), color: palette()[1] },
      { name: "순이익률", type: "line", yAxis: 1, data: points.map((p) => p.margin), color: palette()[2] },
    ],
    yLabel: "", y2Label: "%",
  };
}
/* ── 주가: 일/주/월 집계 + 이동평균 ───────────────────── */
// 서버는 일봉만 준다. 주·월봉은 여기서 묶고, 이동평균은 묶은 뒤의 봉 기준으로 계산한다.
const FREQ_UNIT: Record<string, number> = { d: 1, w: 5, m: 21 }; // 한 봉이 대략 며칠치인지 (거래일)
const MA_WINDOWS = [5, 20, 60, 120];
const MA_COLORS = ["#e0993c", "#3c78e0", "#9b5fd0", "#3fa06a"];

function bucketKey(time: Any, freq: string): string {
  if (freq === "m") return String(time).slice(0, 7);
  const d = new Date(String(time) + "T00:00:00");
  if (isNaN(+d)) return String(time);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); // 그 주의 월요일
  return d.toISOString().slice(0, 10);
}
function aggregate(points: Any[], freq: string): Any[] {
  if (freq !== "w" && freq !== "m") return points;
  const out: Any[] = [];
  let cur: Any = null, key: string | null = null;
  points.forEach((p) => {
    const k = bucketKey(p.time, freq);
    if (k !== key) {
      if (cur) out.push(cur);
      key = k;
      cur = { time: k, open: p.open, high: p.high, low: p.low, close: p.close ?? p.value };
      return;
    }
    cur.high = Math.max(cur.high ?? -Infinity, p.high ?? -Infinity);
    cur.low = Math.min(cur.low ?? Infinity, p.low ?? Infinity);
    cur.close = p.close ?? p.value;
  });
  if (cur) out.push(cur);
  return out;
}
// 단순이동평균 — 앞이 모자란 구간은 null 로 둬 선이 끊긴 채 시작한다.
function sma(values: Array<number | null>, n: number): Array<number | null> {
  const out: Array<number | null> = new Array(values.length).fill(null);
  let sum = 0, cnt = 0;
  for (let i = 0; i < values.length; i++) {
    const v = values[i];
    if (v == null) { sum = 0; cnt = 0; continue; }
    sum += v; cnt++;
    if (cnt > n) { sum -= values[i - n] as number; cnt = n; }
    if (cnt === n) out[i] = sum / n;
  }
  return out;
}
// 화면에 보이는 구간(shown 봉)에 값이 하나도 없는 선은 그리지 않는다.
const MA_UNIT_LABEL: Record<string, string> = { d: "일", w: "주", m: "개월" };
function movingAverages(bars: Any[], shown: number, freq: string) {
  const closes = bars.map((p) => p.close ?? p.value ?? null);
  const unit = MA_UNIT_LABEL[freq || "d"];
  return MA_WINDOWS.map((n, i) => ({
    name: `${n}${unit}`, window: n, color: MA_COLORS[i], data: sma(closes, n).slice(-shown),
  })).filter((m) => m.data.some((v) => v != null));
}

function priceSpec(points: Any[], mode: string, freq: string, shown: number): Any {
  const bars = aggregate(points, freq || "d");
  const n = Math.max(1, Math.min(shown || bars.length, bars.length));
  const view = bars.slice(-n);
  if (mode === "line") {
    return { type: "area", scaleY: true, x: view.map((p) => p.time),
             series: [{ name: "종가", type: "area", data: view.map((p) => p.close ?? p.value),
                        color: palette()[0] }] };
  }
  return { type: "candle", points: view, mas: movingAverages(bars, n, freq || "d") };
}

/* ── 여러 기업 비교 ─────────────────────────────────────
   가격대가 다른 기업을 원화 축에 같이 두면 낮은 쪽이 바닥에 깔려 움직임이
   안 보인다. 기본은 기준일 대비 등락률로 그리고, 원화 축으로도 볼 수 있게 둔다. */
function compareSpec(items: Any[], freq: string, shown: number, axisMode: string): Any {
  const on = items.filter((it) => it.on !== false && (it.points || []).length);
  const byDate = on.map((it) => {
    const bars = aggregate(it.points, freq || "d");
    const m = new Map<string, Any>();
    bars.forEach((p) => m.set(p.time, p.close ?? p.value));
    return { it, m };
  });
  // x 축은 기업들의 날짜 합집합 — 상장 시점이 다르거나 거래정지가 있어도 어긋나지 않는다
  const all = new Set<string>();
  byDate.forEach(({ m }) => m.forEach((_, k) => all.add(k)));
  const x = [...all].sort().slice(-Math.max(1, shown || 1));

  const pct = axisMode !== "won";
  const series = byDate.map(({ it, m }, i) => {
    const raw = x.map((d) => (m.has(d) ? m.get(d) : null));
    const base = raw.find((v) => v != null);
    const data = pct
      ? raw.map((v) => (v == null || !base ? null : (v / base - 1) * 100))
      : raw;
    return { name: it.name || it.symbol, type: "line", data,
             color: it.color || palette()[i % palette().length] };
  });
  // 비교 차트는 여러 종목을 겹친다 — 통화가 섞이면(국내+미국) 절대가격 비교가
  // 무의미하므로 전부 같은 통화일 때만 통화를 넘긴다.
  const ccys = [...new Set(items.map((it: Any) => it.currency).filter(Boolean))];
  return { type: "line", scaleY: true, x, series, currency: ccys.length === 1 ? ccys[0] : "",
           compare: true, pct, yLabel: pct ? "%" : "" };
}

/* ── 인터랙티브 위젯 ────────────────────────────────── */
const FIN_YEARS: Array<[Any, string]> = [[1, "1년"], [2, "2년"], [3, "3년"], [5, "5년"]];
const FIN_PERIOD: Array<[Any, string]> = [["year", "연간"], ["quarter", "분기"]];
const PRICE_DAYS: Array<[Any, string]> = [[60, "3개월"], [120, "6개월"], [250, "1년"], [750, "3년"]];
const PRICE_MODE: Array<[Any, string]> = [["candle", "캔들"], ["line", "라인"]];
const PRICE_FREQ: Array<[Any, string]> = [["d", "일"], ["w", "주"], ["m", "월"]];
// 절대가격 축의 라벨은 통화에 따라 다르다 — 미국 종목에 '원화' 를 띄우면 오해를 준다
const priceAxisOptions = (currency?: string): Array<[Any, string]> =>
  [["pct", "등락률"],
   ["won", currency === "USD" ? "달러" : currency === "KRW" ? "원화" : "가격"]];

function selectEl(name: string, options: Array<[Any, string]>, value: Any): string {
  const opts = options
    .map(([v, t]) => `<option value="${v}"${String(v) === String(value) ? " selected" : ""}>${t}</option>`)
    .join("");
  return `<select class="chart-select" data-ctl="${name}">${opts}</select>`;
}
function segEl(name: string, options: Array<[Any, string]>, value: Any): string {
  return `<div class="chart-seg" data-seg="${name}">` + options
    .map(([v, t]) => `<button type="button" data-val="${v}" class="${String(v) === String(value) ? "on" : ""}">${t}</button>`)
    .join("") + "</div>";
}
function finTable(points: Any[], currency?: string): string {
  const th = points.map((p) => `<th>${p.label}</th>`).join("");
  const row = (label: string, fn: (p: Any) => string) =>
    `<tr><td>${label}</td>${points.map((p) => `<td>${fn(p)}</td>`).join("")}</tr>`;
  return `<div class="chart-table"><table class="art-table">
      <thead><tr><th>항목</th>${th}</tr></thead><tbody>
      ${row("매출", (p) => fmtMoney(p.revenue, currency))}
      ${row("순이익", (p) => fmtMoney(p.netIncome, currency))}
      ${row("순이익률", (p) => (p.margin == null ? "-" : p.margin.toFixed(2) + "%"))}
      </tbody></table></div>`;
}

// 꼬리 질문은 위젯 안에 그리지 않는다 — ChatPage 가 메시지 하단 한 곳에 모은다.
const escapeAttr = (t: Any) =>
  String(t).replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#039;" })[c] as string);

async function loadFinancials(state: Any) {
  if (!fetcher) return null;
  const q = new URLSearchParams({ period: state.period, years: String(state.years) });
  if (state.symbol) q.set("symbol", state.symbol);
  if (state.name) q.set("name", state.name);
  const d = await fetcher(`/agent/financials?${q}`);
  return d && d.ok ? d.points : null;
}
// 화면에 보일 봉 수 (일봉 기준 기간 → 주·월봉으로 환산)
const shownBars = (state: Any) => Math.max(5, Math.round(state.days / FREQ_UNIT[state.freq || "d"]));
// 120봉 이동평균이 왼쪽 끝부터 그려지려면 표시 구간보다 120봉 앞선 데이터가 더 필요하다.
// 다만 월봉 120개월(=10년)까지 받으면 최초 조회가 6초를 넘어 1500일(약 6년)에서 끊는다.
// 데이터가 모자란 이동평균선은 그리지 않으므로, 월봉에서는 60개월선까지 표시된다.
const neededDaily = (state: Any) => Math.min(1500, state.days + 120 * FREQ_UNIT[state.freq || "d"]);

const hasOHLC = (pts: Any[]) => !!(pts && pts.length && pts[0].open != null && pts[0].close != null);

/* ── 비교 기업 칩 ───────────────────────────────────── */
const MAX_COMPARE = 5;
const initial = (name: Any) => String(name || "?").trim().charAt(0) || "?";

// 배지 색은 차트 선 색이다 — 칩과 선을 잇는 유일한 단서라 로고로 덮으면 안 된다.
// 그래서 색 원을 그대로 두고 그 안쪽에 로고를 얹는다(테두리 2px 이 색으로 남는다).
// 로고가 403 이면 img 가 스스로 빠지고 원래의 색+이니셜 배지가 그대로 드러난다.
function badgeHtml(name: Any, symbol: Any, color: string): string {
  const url = logoUrl(symbol);
  // loading="lazy" 를 붙이면 안 된다 — 배지가 18px 이고 overflow:hidden 안에 절대배치라
  // 크로뮴이 교차 판정을 못 해 **fetch 자체를 시작하지 않는다**(currentSrc 가 빈 채로
  // 남는다). 칩은 최대 5개라 지연 로딩으로 아낄 것도 없다.
  const img = url && !logoFailed(url)
    ? `<img class="cmp-logo" src="${escapeAttr(url)}" alt="" decoding="async">`
    : "";
  return `<span class="cmp-badge" style="background:${color}">${escapeAttr(initial(name))}${img}</span>`;
}

/** 로고가 없는 기업은 img 를 지워 색+이니셜 배지로 되돌린다 (렌더할 때마다 호출) */
function wireLogos(root: HTMLElement): void {
  root.querySelectorAll<HTMLImageElement>("img.cmp-logo").forEach((img) => {
    img.addEventListener("error", () => {
      markLogoFailed(img.src);
      img.remove();
    }, { once: true });
  });
}

function chipsHtml(items: Any[]): string {
  const chips = items
    .map((it, i) => {
      const color = it.color || palette()[i % palette().length];
      return `<button type="button" class="cmp-chip${it.on === false ? " off" : ""}" data-cmp="${escapeAttr(it.symbol)}"
                title="${escapeAttr(it.name)} 표시 켜기/끄기">
        ${badgeHtml(it.name, it.symbol, color)}
        <span class="cmp-name">${escapeAttr(it.name)}</span>
        ${items.length > 1 ? `<span class="cmp-x" data-rm="${escapeAttr(it.symbol)}" role="button"
            title="${escapeAttr(it.name)} 빼기" aria-label="${escapeAttr(it.name)} 빼기">×</span>` : ""}
      </button>`;
    })
    .join("");
  const add = items.length >= MAX_COMPARE
    ? `<span class="cmp-full">최대 ${MAX_COMPARE}개까지 비교할 수 있습니다</span>`
    : `<button type="button" class="cmp-add">+ 비교 기업 추가</button>
         <span class="cmp-search" hidden>
           <input type="text" class="cmp-input" placeholder="기업명 입력" aria-label="비교할 기업 검색">
           <span class="cmp-results" hidden></span>
         </span>`;
  return `<div class="cmp-bar">${chips}${add}</div>`;
}

async function loadPriceFor(symbol: string, state: Any) {
  if (!fetcher) return null;
  const d = await fetcher(
    `/agent/stock-price?symbol=${encodeURIComponent(symbol)}&days=${neededDaily(state)}`);
  return d && d.ok ? d.points : null;
}

function widget(box: HTMLElement, spec: Any) {
  const kind = spec.type === "price" ? "price" : "financials";
  // 비교 대상은 items 로 다룬다. 기존 단일 스펙(symbol/points)도 items 하나로 감싼다.
  const initialItems = (spec.items && spec.items.length ? spec.items : [{
    symbol: spec.symbol || "", name: spec.name || spec.title || spec.symbol || "", points: spec.points || [],
  }]).slice(0, MAX_COMPARE).map((it: Any, i: number) => Object.assign(
    { on: true, points: [], color: palette()[i % palette().length] }, it));

  const state: Any = kind === "price"
    ? { days: spec.days || 250, freq: spec.freq || "d", axis: "pct",
        mode: hasOHLC(initialItems[0].points) ? "candle" : "line", items: initialItems }
    : { symbol: spec.symbol || "", name: spec.name || "", period: spec.period || "year",
        years: spec.years || 3, points: spec.points || [] };

  const activeCount = () => state.items.filter((it: Any) => it.on !== false).length;
  const single = () => activeCount() <= 1;

  const controls = kind === "price"
    ? selectEl("days", PRICE_DAYS, state.days) + segEl("freq", PRICE_FREQ, state.freq)
      + segEl("axis", priceAxisOptions(spec.currency), state.axis) + segEl("mode", PRICE_MODE, state.mode)
    : selectEl("years", FIN_YEARS, state.years) + selectEl("period", FIN_PERIOD, state.period);

  box.innerHTML =
    `<div class="chart-widget">
         <div class="chart-head">
           ${spec.title ? `<div class="chart-title">${spec.title}</div>` : "<div></div>"}
           <div class="chart-controls">${controls}</div>
         </div>
         ${kind === "price" ? chipsHtml(state.items) : ""}
         <div class="chart-canvas" style="height:${spec.height || (kind === "price" ? 300 : 260)}px"></div>
         <div class="chart-foot"></div>
       </div>`;
  const canvas = box.querySelector(".chart-canvas") as HTMLElement;
  const foot = box.querySelector(".chart-foot") as HTMLElement;

  function renderChips() {
    const bar = box.querySelector(".cmp-bar");
    if (bar) bar.outerHTML = chipsHtml(state.items);
    wireLogos(box);
  }
  wireLogos(box);   // 최초 렌더분
  // 캔들은 한 기업일 때만. 비교 중에는 등락률/원화 축을 고를 수 있다.
  function syncControls() {
    const one = single();
    const btn = (seg: string, val: string) =>
      box.querySelector(`.chart-seg[data-seg="${seg}"] button[data-val="${val}"]`) as HTMLButtonElement | null;
    const ohlc = one && hasOHLC((state.items.find((it: Any) => it.on !== false) || {}).points);
    const candle = btn("mode", "candle");
    if (candle) {
      candle.disabled = !ohlc;
      candle.title = one
        ? (ohlc ? "" : "이 기간에는 시·고·저가 데이터가 없어 라인으로만 볼 수 있습니다")
        : "여러 기업을 비교할 때는 라인 차트로만 볼 수 있습니다";
    }
    if (!ohlc && state.mode === "candle") {
      state.mode = "line";
      box.querySelectorAll('.chart-seg[data-seg="mode"] button')
        .forEach((b) => b.classList.toggle("on", (b as HTMLElement).dataset.val === "line"));
    }
    const axisSeg = box.querySelector('.chart-seg[data-seg="axis"]') as HTMLElement | null;
    if (axisSeg) axisSeg.hidden = one; // 한 기업이면 정규화가 의미 없다
  }

  function paint() {
    if (kind === "financials") {
      if (!state.points || !state.points.length) return;
      draw(canvas, financialsSpec(state.points, spec.currency));
      foot.innerHTML = finTable(state.points, spec.currency);
      return;
    }
    syncControls();
    const shown = shownBars(state);
    if (single()) {
      const it = state.items.find((x: Any) => x.on !== false) || state.items[0];
      if (!it || !(it.points || []).length) return;
      draw(canvas, priceSpec(it.points, state.mode, state.freq, shown));
    } else {
      draw(canvas, compareSpec(state.items, state.freq, shown, state.axis));
    }
    foot.innerHTML = "";
  }

  async function reload() {
    box.classList.add("loading");
    try {
      if (kind === "financials") {
        const pts = await loadFinancials(state);
        if (pts && pts.length) state.points = pts;
      } else {
        const targets = state.items.filter((it: Any) => it.symbol);
        const got = await Promise.all(
          targets.map((it: Any) => loadPriceFor(it.symbol, state).catch(() => null)));
        got.forEach((pts: Any, i: number) => { if (pts && pts.length) targets[i].points = pts; });
      }
      paint();
    } catch { /* 실패 시 기존 데이터를 유지한다 */ }
    box.classList.remove("loading");
  }

  async function addCompany(symbol: string, name: string) {
    if (state.items.length >= MAX_COMPARE) return;
    if (state.items.some((it: Any) => it.symbol === symbol)) return;
    const it: Any = { symbol, name, on: true, points: [],
                      color: palette()[state.items.length % palette().length] };
    state.items.push(it);
    renderChips();
    box.classList.add("loading");
    try {
      const pts = await loadPriceFor(symbol, state);
      if (pts && pts.length) it.points = pts;
    } catch { /* noop */ }
    box.classList.remove("loading");
    paint();
  }

  /* ── 기업 검색 (이름 입력 → 후보) ─────────────────── */
  let searchTimer: ReturnType<typeof setTimeout> | undefined;
  box.addEventListener("input", (e) => {
    const inp = (e.target as HTMLElement).closest(".cmp-input") as HTMLInputElement | null;
    if (!inp) return;
    const results = box.querySelector(".cmp-results") as HTMLElement;
    const q = inp.value.trim();
    clearTimeout(searchTimer);
    if (q.length < 2) { results.hidden = true; results.innerHTML = ""; return; }
    searchTimer = setTimeout(async () => {
      try {
        const d = fetcher ? await fetcher(`/agent/company-search?q=${encodeURIComponent(q)}`) : null;
        const list = d && d.ok && d.items ? d.items : [];
        const have = new Set(state.items.map((it: Any) => it.symbol));
        const rows = list.filter((c: Any) => !have.has(c.symbol));
        results.innerHTML = rows.length
          ? rows.map((c: Any) => `<button type="button" class="cmp-hit" data-sym="${escapeAttr(c.symbol)}"
                 data-name="${escapeAttr(c.name)}">${escapeAttr(c.name)}<em>${escapeAttr(c.symbol)}</em></button>`).join("")
          : `<span class="cmp-empty">결과가 없습니다</span>`;
        results.hidden = false;
      } catch { results.hidden = true; }
    }, 250);
  });

  box.addEventListener("change", (e) => {
    const sel = (e.target as HTMLElement).closest(".chart-select") as HTMLSelectElement | null;
    if (!sel) return;
    const ctl = sel.dataset.ctl as string;
    state[ctl] = ctl === "period" ? sel.value : Number(sel.value);
    void reload();
  });

  box.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;
    const ask = target.closest("[data-ask]") as HTMLElement | null;
    if (ask) {
      document.dispatchEvent(new CustomEvent("va:ask", { detail: { text: ask.dataset.ask } }));
      return;
    }
    const rm = target.closest("[data-rm]") as HTMLElement | null;
    if (rm) {
      e.stopPropagation();
      state.items = state.items.filter((it: Any) => it.symbol !== rm.dataset.rm);
      renderChips();
      paint();
      return;
    }
    const chip = target.closest(".cmp-chip") as HTMLElement | null;
    if (chip) {
      const it = state.items.find((x: Any) => x.symbol === chip.dataset.cmp);
      // 마지막 하나까지 끄면 그릴 게 없어진다
      if (it && !(it.on !== false && activeCount() === 1)) {
        it.on = it.on === false;
        renderChips();
        paint();
      }
      return;
    }
    if (target.closest(".cmp-add")) {
      (box.querySelector(".cmp-add") as HTMLElement).hidden = true;
      const wrap = box.querySelector(".cmp-search") as HTMLElement;
      wrap.hidden = false;
      (wrap.querySelector(".cmp-input") as HTMLInputElement).focus();
      return;
    }
    const hit = target.closest(".cmp-hit") as HTMLElement | null;
    if (hit) { void addCompany(hit.dataset.sym as string, hit.dataset.name as string); return; }

    const b = target.closest(".chart-seg button") as HTMLButtonElement | null;
    if (!b || b.disabled) return;
    const seg = b.closest("[data-seg]") as HTMLElement;
    state[seg.dataset.seg as string] = b.dataset.val;
    seg.querySelectorAll("button").forEach((x) => x.classList.toggle("on", x === b));
    // 캔들/라인·축은 가진 데이터로 바로 다시 그리고, 주기는 앞선 데이터가 더 필요해 재조회한다
    if (seg.dataset.seg === "freq") void reload();
    else paint();
  });

  paint();
  // 주가는 심볼만 있으면 선택 기간 기준으로 최신 데이터를 다시 받는다
  const needFetch = kind === "price"
    ? state.items.some((it: Any) => it.symbol && !it.points.length) || state.items.some((it: Any) => it.symbol)
    : !state.points.length;
  if (needFetch) void reload();
}

/* ── 한 답변에 차트 하나 ─────────────────────────────── */
/** export 는 테스트용 — 유형별 생존 규칙 회귀 고정 (지도가 자동 막대에 밀리던 버그) */
export function priority(spec: Any): number {
  if (spec.type === "price") return 3;
  // 지도는 툴이 의도를 갖고 내려준 것 — 답변 본문 표에서 자동 생성된 막대차트
  // (x 있음 → 2+)에 밀려 제거되면 안 된다. 기본식은 지도를 1로 계산해
  // '지역별 분포' 답변에서 지도가 사라졌다 (2026-08-19 dev 실측).
  if (spec.type === "krmap") return 2.9;
  if (spec.type === "financials") return 2.5;
  const pts = ((spec.series || [])[0]?.data || []).length;
  return ((spec.x && spec.x.length >= 3) ? 2 : 1) + Math.min(pts, 60) / 1000;
}
export function dispose(box: ParentNode) {
  for (let i = instances.length - 1; i >= 0; i--) {
    if (!box.contains(instances[i].el)) continue;
    dropInstance(i);
  }
}

/** React 리렌더로 DOM 에서 떨어져 나간 인스턴스 정리 (바닐라엔 없던 누수 방지) */
function prune() {
  for (let i = instances.length - 1; i >= 0; i--) {
    if (!instances[i].el.isConnected) dropInstance(i);
  }
}

export function hydrate(root?: ParentNode | null) {
  prune();
  const scopes = new Set<ParentNode>();
  (root || document).querySelectorAll(".chart-box[data-chart]").forEach((b) => {
    scopes.add(b.closest("article") || document.body);
  });
  scopes.forEach((scope) => {
    interface Parsed { box: HTMLElement; spec: Any; p: number }
    const parsed: Parsed[] = [];
    let best: Parsed | null = null;
    scope.querySelectorAll(".chart-box[data-chart]").forEach((node) => {
      const box = node as HTMLElement;
      let spec: Any;
      try { spec = JSON.parse(decodeURIComponent(box.dataset.chart as string)); } catch { box.remove(); return; }
      const p = priority(spec);
      parsed.push({ box, spec, p });
      if (!best || p > best.p) best = { box, spec, p };
    });
    // 한 답변에 여러 기업의 주가가 오면 버리지 않고 하나의 비교 차트로 합친다.
    // ("네이버와 카카오 비교해줘" 처럼 물었을 때 한 기업만 남던 문제)
    const prices = parsed.filter((x) => x.spec.type === "price" && x.spec.symbol);
    if (best && (best as Parsed).spec.type === "price" && prices.length > 1) {
      const seen = new Set<string>();
      const items: Any[] = [];
      prices.forEach(({ spec }) => {
        if (seen.has(spec.symbol)) return;
        seen.add(spec.symbol);
        items.push({ symbol: spec.symbol, name: spec.name || spec.title || spec.symbol,
                     points: spec.points || [] });
      });
      best = { box: (best as Parsed).box, p: (best as Parsed).p,
               spec: Object.assign({}, (best as Parsed).spec, {
                 title: "주가 비교", items: items.slice(0, MAX_COMPARE),
                 symbol: undefined, points: undefined, followups: undefined,
               }) };
    }
    parsed.forEach(({ box, spec }) => {
      if (best && box === best.box) return;
      dispose(box);
      // 비교 대상 주가 차트는 지우지 않고 비워서 숨긴다. 아티팩트는 하나씩
      // 도착하므로, 지워버리면 세 번째 기업이 올 때 앞서 합친 기업을 잃는다.
      if (spec.type === "price" && spec.symbol) { box.innerHTML = ""; box.hidden = true; return; }
      box.remove();
    });
    if (!best) return;
    const { box, spec } = best as Parsed;
    // 아티팩트는 하나씩 도착하고 그때마다 hydrate 가 다시 돈다. 이미 그렸다는
    // 표시만 보고 건너뛰면, 뒤늦게 온 기업이 비교 차트에 합쳐지지 못한다.
    // 무엇을 그렸는지까지 담아두고 내용이 달라졌을 때만 다시 그린다.
    const drawnKey = spec.items
      ? "cmp:" + spec.items.map((i: Any) => i.symbol).sort().join(",")
      : (spec.type || "") + ":" + (spec.symbol || spec.name || "") + ":" + (spec.title || "");
    if (box.getAttribute("data-drawn") === drawnKey) return;
    dispose(box);
    box.setAttribute("data-drawn", drawnKey);
    // 재무·주가는 컨트롤이 붙은 위젯, 그 외는 단순 차트
    if (spec.type === "price" || spec.type === "financials") { widget(box, spec); return; }
    const title = spec.title ? `<div class="chart-title">${spec.title}</div>` : "";
    box.innerHTML = `${title}<div class="chart-canvas" style="height:${spec.height || 260}px"></div>`;
    draw(box.querySelector(".chart-canvas") as HTMLElement, spec);
  });
}

export function refresh() {
  prune();
  instances.forEach((r) => {
    try { r.inst.setOption(toOption(r.spec), true); } catch { /* noop */ }
  });
}
export function resize() {
  instances.forEach((r) => {
    try { r.inst.resize(); } catch { /* noop */ }
  });
}
if (typeof window !== "undefined") window.addEventListener("resize", resize);

export const VAChart = { hydrate, refresh, resize, toOption, dispose, setFetcher };
